import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    useTheme,
    Typography,
    Stack
} from '@mui/material';
import CommonCard from '../../../../components/card/CommonCard';
import Dropdown from '../../../../components/dropdown';
import DataItem from '../../../../components/dataItem';
import { Info, Point } from '../../../../assets/images';
import { useTranslation } from 'react-i18next';
import { logAPI } from '../../../../actions/imsServer';
import { POINT_LOG_TYPES, API_PAGE_SIZE } from '../../../../constants';
import { formatDateTimeDisplay, getLabelByValue, formatChallengePointMessage } from '../../../../utils/base';

export const PointTab = ({ userData }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [periodFilter, setPeriodFilter] = useState(7);
    const [typeFilter, setTypeFilter] = useState(-1); // -1: 전체, 0: 적립, 2: 사용

    // 실제 데이터 상태
    const [pointLogs, setPointLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [monthlyExpiry, setMonthlyExpiry] = useState(0);

    // 드롭다운 옵션 (value: 내부 키, label: 번역 키 — Dropdown 내부에서 t 적용)
    const periodOptions = [
        { value: 7, label: 'last_7_days' },
        { value: 30, label: 'last_30_days' },
        { value: 90, label: 'last_90_days' },
    ];

    // 이달 소멸예정 포인트 계산
    const calculateMonthlyExpiry = (logs) => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-11

        // 2년 전 같은 달의 적립 포인트들 찾기
        const twoYearsAgo = currentYear - 2;

        let expiryPoints = 0;

        // 날짜순으로 정렬 (오래된 것부터)
        const sortedLogs = [...logs].sort((a, b) => new Date(a.time) - new Date(b.time));

        // 2년 전 같은 달에 적립된 포인트들을 추적
        const expiryCandidates = [];

        sortedLogs.forEach(log => {
            const logDate = new Date(log.time);
            const logYear = logDate.getFullYear();
            const logMonth = logDate.getMonth();

            if (log.point > 0) { // 적립 포인트
                // 2년 전 같은 달에 적립된 포인트인지 확인
                if (logYear === twoYearsAgo && logMonth === currentMonth) {
                    expiryCandidates.push({
                        amount: log.point,
                        date: logDate,
                        used: false
                    });
                }
            } else if (log.point < 0) { // 사용 포인트
                // 사용된 포인트를 선입선출로 차감
                let remainingUsage = Math.abs(log.point);

                for (let i = 0; i < expiryCandidates.length && remainingUsage > 0; i++) {
                    if (!expiryCandidates[i].used) {
                        const availableAmount = expiryCandidates[i].amount;
                        const deduction = Math.min(availableAmount, remainingUsage);

                        expiryCandidates[i].amount -= deduction;
                        remainingUsage -= deduction;

                        if (expiryCandidates[i].amount <= 0) {
                            expiryCandidates[i].used = true;
                        }
                    }
                }
            }
        });

        // 사용되지 않은 소멸예정 포인트 합산
        expiryPoints = expiryCandidates
            .filter(candidate => !candidate.used)
            .reduce((sum, candidate) => sum + candidate.amount, 0);

        return expiryPoints;
    };

    // 실제 데이터 가져오기
    const fetchPointLogs = async () => {
        if (!userData?.id) return;

        try {
            setLoading(true);
            setError(null);

            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const res = await logAPI.getPointLogs({
                    userId: userData.id,
                    offset
                });
                page = res?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            setPointLogs(allLogs);
            const expiryPoints = calculateMonthlyExpiry(allLogs);
            setMonthlyExpiry(expiryPoints);
        } catch (err) {
            console.error('포인트 로그 데이터 로딩 오류:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPointLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPointLogs는 의존성에서 제외
    }, [userData?.id]);

    // 필터링된 포인트 로그 계산
    const filteredPointLogs = useMemo(() => {
        let filteredLogs = [...pointLogs];

        // 기간 필터 적용
        const now = new Date();
        const startDate = new Date(now.getTime() - periodFilter * 24 * 60 * 60 * 1000);

        filteredLogs = filteredLogs.filter(log => {
            const logDate = new Date(log.time);
            return logDate >= startDate;
        });

        // 타입 필터 적용
        if (typeFilter !== -1) {
            filteredLogs = filteredLogs.filter(log => log.type === typeFilter);
        }

        // 날짜순으로 정렬 (최신순)
        filteredLogs.sort((a, b) => new Date(b.time) - new Date(a.time));

        return filteredLogs;
    }, [pointLogs, periodFilter, typeFilter]);

    // 월간 차트 데이터 생성 (현재 월 기준)
    const monthlyChartData = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        // 날짜별로 데이터 집계
        const chartDataMap = new Map();

        // 초기화: 모든 날짜에 대해 0으로 시작
        for (let day = 1; day <= daysInMonth; day++) {
            chartDataMap.set(day, { day, earned: 0, used: 0 });
        }

        // 포인트 로그를 날짜별로 집계
        pointLogs.forEach(log => {
            const logDate = new Date(log.time);
            if (logDate.getFullYear() === year && logDate.getMonth() === month) {
                const day = logDate.getDate();
                if (chartDataMap.has(day)) {
                    const data = chartDataMap.get(day);
                    if (log.point > 0) {
                        data.earned += log.point;
                    } else if (log.point < 0) {
                        data.used += Math.abs(log.point);
                    }
                }
            }
        });

        return Array.from(chartDataMap.values());
    }, [pointLogs]);

    // 차트 최대값 계산 (Y축 최대값 결정용)
    const chartMaxValue = useMemo(() => {
        if (monthlyChartData.length === 0) return 600;
        const maxEarned = Math.max(...monthlyChartData.map(d => d.earned));
        const maxUsed = Math.max(...monthlyChartData.map(d => d.used));
        const maxValue = Math.max(maxEarned, maxUsed);
        // 최대값을 기준으로 Y축을 적절히 설정 (600, 500, 400, 300, 200, 100 등)
        if (maxValue === 0) return 600;
        return Math.ceil(maxValue / 100) * 100;
    }, [monthlyChartData]);

    // 포인트 데이터
    const pointData = {
        currentPoints: userData?.point || 0,
        monthlyExpiry: monthlyExpiry,
    };

    const RewardHistoryItem = ({ item }) => {
        const isEarn = item.point > 0;
        const pointColor = isEarn ? theme.palette.figma.steps : theme.palette.figma.calorieRed;

        return (
            <Box
                sx={{
                    borderBottom: '1px dashed ' + theme.palette.figma.white[30],
                    padding: '10px 20px',
                    minHeight: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Stack spacing={0.5}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: theme.palette.figma.white[80],
                            letterSpacing: '-0.42px',
                            textAlign: 'left',
                        }}
                    >
                        {item.message ? formatChallengePointMessage(item.message, t, i18n, item) : t('point_transaction')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 400,
                            color: theme.palette.figma.white[60],
                            letterSpacing: '-0.36px',
                            textAlign: 'left',
                        }}
                    >
                        {formatDateTimeDisplay(item.time, 'datetime')}
                    </Typography>
                </Stack>
                <Stack spacing={0.5} alignItems="flex-end">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: pointColor,
                            letterSpacing: '-0.42px',
                        }}
                    >
                        {isEarn ? '+' : ''}{item.point?.toLocaleString()} P
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 400,
                            color: theme.palette.figma.white[60],
                            letterSpacing: '-0.36px',
                        }}
                    >
                        {(() => { const lbl = getLabelByValue(POINT_LOG_TYPES, item.type); return lbl ? t(lbl) : t('complete'); })()}
                    </Typography>
                </Stack>
            </Box>
        );
    };

    const dataItemSx = {
        justifyContent: 'flex-start',
    };

    const dataItemValueSx = {
        color: theme.palette.figma.white[60],
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '-0.42px',
    };

    const dataItemUnitSx = {
        color: theme.palette.figma.white[100],
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 700,
        letterSpacing: '-0.42px',
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 메인 콘텐츠 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                {/* 좌측 메인 영역 */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* 포인트 카드 */}
                    <CommonCard
                        sx={{
                            backgroundColor: theme.palette.figma.block,
                            borderRadius: '20px',
                            padding: '20px',
                            boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {/* 보유 포인트 */}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 2 }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Point size={20} color={'#FC0'} />
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        color: theme.palette.figma.white[60],
                                        letterSpacing: '-0.42px',
                                    }}
                                >
                                    {t('available_points')}
                                </Typography>
                            </Stack>
                            <Typography
                                sx={{
                                    fontSize: '30px',
                                    fontWeight: 600,
                                    letterSpacing: '-0.9px',
                                    color: theme.palette.figma.white[100]
                                }}
                            >
                                {pointData.currentPoints.toLocaleString()} P
                            </Typography>
                        </Stack>

                        {/* 이달 소멸예정 */}
                        <Box
                            sx={{
                                backgroundColor: theme.palette.figma.white[10],
                                borderRadius: '10px',
                                padding: '10px',
                                position: 'relative',
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-start">
                                <Info size={20} />
                                <DataItem
                                    sx={dataItemSx}
                                    valueSx={dataItemValueSx}
                                    unitSx={dataItemUnitSx}
                                    value={t('expiring_this_month')}
                                    unit={pointData.monthlyExpiry}
                                />
                            </Stack>
                        </Box>
                    </CommonCard>

                    {/* 필터 드롭다운 */}
                    <CommonCard>
                        <Stack direction="row" spacing={2} sx={{ padding: '10px' }}>
                            <Dropdown
                                options={periodOptions}
                                value={periodFilter}
                                onChange={(option) => setPeriodFilter(option.value)}
                                sx={{ minWidth: '124px' }}
                            />
                            <Dropdown
                                options={POINT_LOG_TYPES}
                                value={typeFilter}
                                onChange={(option) => setTypeFilter(option.value)}
                                sx={{ minWidth: '124px' }}
                            />
                        </Stack>

                        <Stack spacing={0} sx={{ width: '100%' }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <Typography>{t('loading')}</Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <Typography color="error">{t('error')}: {error}</Typography>
                                </Box>
                            ) : filteredPointLogs.length > 0 ? (
                                filteredPointLogs.map((item, index) => (
                                    <RewardHistoryItem key={item.id || index} item={item} />
                                ))
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <Typography>{t('no_point_history')}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </CommonCard>
                </Box>

                {/* 우측 차트 영역 */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* 월간 포인트 차트 */}
                    <CommonCard>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            {t('monthly_point_status')}
                        </Typography>

                        {/* 차트 영역 */}
                        <Box sx={{ position: 'relative', height: 300, width: '100%' }}>
                            {/* Y축 라벨 */}
                            <Box sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '10px 0'
                            }}>
                                {[chartMaxValue, chartMaxValue * 0.8, chartMaxValue * 0.6, chartMaxValue * 0.4, chartMaxValue * 0.2, 0].map((value) => (
                                    <Typography
                                        key={value}
                                        variant="caption"
                                        sx={{
                                            fontSize: '10px',
                                            color: theme.palette.figma.white[60],
                                            fontWeight: 500
                                        }}
                                    >
                                        {Math.round(value)}
                                    </Typography>
                                ))}
                            </Box>

                            {/* 차트 그리드 라인 */}
                            <Box sx={{
                                position: 'absolute',
                                left: 0,
                                right: '30px',
                                top: 0,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}>
                                {[chartMaxValue, chartMaxValue * 0.8, chartMaxValue * 0.6, chartMaxValue * 0.4, chartMaxValue * 0.2, 0].map((value, index) => (
                                    <Box
                                        key={value}
                                        sx={{
                                            height: '1px',
                                            backgroundColor: theme.palette.figma.white[10],
                                            borderStyle: 'dashed',
                                            borderWidth: '0 0 1px 0',
                                            borderColor: theme.palette.figma.white[10]
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* 막대 차트 */}
                            <Box sx={{
                                position: 'absolute',
                                left: 0,
                                right: '30px',
                                top: 0,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '2px',
                                padding: '10px 0'
                            }}>
                                {monthlyChartData.map((data, index) => {
                                    const earnedHeight = chartMaxValue > 0 ? `${(data.earned / chartMaxValue) * 100}%` : '0%';
                                    const usedHeight = chartMaxValue > 0 ? `${(data.used / chartMaxValue) * 100}%` : '0%';

                                    return (
                                        <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '1px', height: '100%' }}>
                                            {/* 적립 포인트 (초록색) */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: earnedHeight,
                                                    backgroundColor: theme.palette.figma.steps,
                                                    borderRadius: '2px 2px 0 0',
                                                    minHeight: data.earned > 0 ? '2px' : '0px'
                                                }}
                                            />
                                            {/* 사용 포인트 (회색) */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: usedHeight,
                                                    backgroundColor: theme.palette.figma.white[30],
                                                    borderRadius: '0 0 2px 2px',
                                                    minHeight: data.used > 0 ? '2px' : '0px'
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* X축 라벨 */}
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: '30px',
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '2px',
                                padding: '5px 0'
                            }}>
                                {monthlyChartData.map((data, index) => {
                                    const daysInMonth = monthlyChartData.length;
                                    const labelCount = 7;
                                    const step = Math.floor(daysInMonth / (labelCount - 1));
                                    const shouldShowLabel = index === 0 ||
                                        index === daysInMonth - 1 ||
                                        (index % step === 0 && index < daysInMonth - 1);

                                    return (
                                        <Box key={index} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                            {shouldShowLabel && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[60],
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {data.day}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </CommonCard>
                </Box>
            </Box>
        </Box>
    );
};
